import { useEffect } from 'react';
import { useNotify } from 'react-admin';

import { supplyGet } from './supplyApi';
import { beginRecord, setLoaded, setLoadFailed } from './softGoodsStore';

/**
 * Seeds the soft goods pickers from the adjustment ledger when a donation is
 * opened.
 *
 * The lots a donation used are not stored on the processing row - only their
 * lot numbers as text are. The authoritative record is the ledger, so on open
 * we ask the API what this donation is currently holding. That is what lets an
 * edit adjust inventory by the difference instead of consuming everything a
 * second time.
 *
 * The result goes into softGoodsStore rather than form state, so opening a
 * record does not mark it dirty.
 *
 * Renders nothing.
 */
const SoftGoodUsageLoader = ({ record, recordId, refType = 'PROCESSING' }) => {
    const notify = useNotify();

    // react-admin injects `record` into direct form children; `recordId` is
    // there for the places this sits deeper in the tree.
    const id = recordId || (record && record.id) || null;

    useEffect(() => {
        beginRecord(id);

        if (!id) {
            // Creating a donation: nothing has been consumed yet, so an empty
            // selection is already the complete picture.
            setLoaded(null, [], {});
            return undefined;
        }

        let cancelled = false;

        supplyGet(`supplyconsumption/${refType}/${id}`)
            .then(result => {
                if (cancelled) { return; }

                const rows = (result && result.lines) || [];
                const committed = {};

                // Lines whose supply type has since been unmapped are kept even
                // though no picker will show them: dropping them here would
                // give that stock back on the next save.
                const lines = rows
                    .filter(row => Number(row.qty_used) > 0)
                    .map(row => {
                        committed[row.receipt_id] = Number(row.qty_used) || 0;
                        return {
                            phlebo_field: row.phlebo_field,
                            receipt_id: row.receipt_id,
                            lot_no: row.lot_no,
                            supply_type: row.supply_type,
                            expiry_date: row.expiry_date,
                            qty: Number(row.qty_used) || 0,
                        };
                    });

                setLoaded(id, lines, committed);
            })
            .catch(error => {
                if (cancelled) { return; }
                // Not fatal: the tab still works, the pickers just start empty
                // and the save leaves inventory untouched. Warn anyway, because
                // lot numbers edited from here would not reach the ledger.
                setLoadFailed(id);
                console.log('Could not load soft goods usage', error);
                notify(
                    'Could not load the soft goods already recorded for this donation. '
                    + 'Reload before changing lot numbers.',
                    'warning'
                );
            });

        return () => { cancelled = true; };
    }, [id, refType, notify]);

    return null;
};

export default SoftGoodUsageLoader;
